import { Select } from "@mantine/core";
import {
  format,
  isValid,
  parseISO,
} from "date-fns";
import {
  useEffect, useMemo, useState, memo, useCallback,
} from "react";
import dateConverter from "../../utils/dateConverter";
import FormInput from "../ui/inputs/text/FormInput";
import { FILTER_COMBOBOX_PROPS, FILTER_SELECT_CLASSES } from "./filterStyles";

type Props = {
  handleChange: (
    operator: string,
    value: any,
    type: string,
    entityType: string,
  ) => void;
  fieldSelect: boolean;
  op?: string;
  value?: any;
};

// Move constants outside component
const TIME_FRAME = [
  "Last day",
  "Last 7 days",
  "Last 30 days",
  "Last 90 days",
  "Last 180 days",
  "Last 365 days",
  "Next day",
  "Next 7 days",
  "Next 30 days",
  "Next 90 days",
  "Next 180 days",
  "Next 365 days",
].map((key) => ({ label: key, value: key }));

const RELATIVE_OPS = [
  { label: "within", value: "relative" },
  { label: "before", value: "before" },
  { label: "after", value: "after" },
];

const ABSOLUTE_OPS = [
  { label: "is equal to", value: "on" },
  { label: "is greater than", value: "after" },
  { label: "is less than", value: "before" },
  { label: "is in range", value: "between" },
];

const DateFilter = memo((props: Props) => {
  const {
    op,
    value,
    handleChange,
    fieldSelect,
  } = props;

  const [changeType, setChangeType] = useState<any>(
    !op || (op === "within" || op === "before" || op === "relative")
      ? "relative" : "absolute",
  );

  const ops = useMemo(
    () => (changeType === "relative" ? RELATIVE_OPS : ABSOLUTE_OPS),
    [changeType],
  );
  const [relativeRange, setRelativeRange] = useState<string>(
    value && changeType === "relative" ? (value?.includes("day") ? value : undefined) : undefined
  );
  const [firstDate, setFirstDate] = useState<any>(
    value?.start ? format(parseISO(value.start), "yyyy-MM-dd")
      : (value && changeType === "absolute" && isValid(parseISO(value)) ? format(parseISO(value), "yyyy-MM-dd") : undefined)
  );
  const [secondDate, setSecondDate] = useState<any>(
    value?.end ? format(parseISO(value?.end), "yyyy-MM-dd") : undefined
  );
  const [selectOp, setSelectOp] = useState<any>(
    ops.find((item) => item.value === op)?.value || ""
  );

  // Sync state with props (only update state, don't call handleChange)
  // Only depend on props, not state, to avoid loops
  useEffect(() => {
    const foundOp = ops.find((item) => item.value === op);
    if (foundOp) {
      setSelectOp((prev: any) => {
        if (prev !== foundOp.value) {
          return foundOp.value;
        }
        return prev;
      });
    }
  }, [op, ops]);

  useEffect(() => {
    if (changeType === "relative" && value?.includes("day")) {
        setRelativeRange((prev: string | undefined) => {
        if (prev !== value) {
          return value;
        }
        return prev;
      });
    } else if (changeType === "absolute") {
      if (value?.start) {
        const formatted = format(parseISO(value.start), "yyyy-MM-dd");
        setFirstDate((prev: any) => {
          if (prev !== formatted) {
            return formatted;
          }
          return prev;
        });
      } else if (value && isValid(parseISO(value))) {
        const formatted = format(parseISO(value), "yyyy-MM-dd");
        setFirstDate((prev: any) => {
          if (prev !== formatted) {
            return formatted;
          }
          return prev;
        });
      }
      if (value?.end) {
        const formatted = format(parseISO(value.end), "yyyy-MM-dd");
        setSecondDate((prev: any) => {
          if (prev !== formatted) {
            return formatted;
          }
          return prev;
        });
      }
    }
  }, [value, changeType]);

  // Reset selectOp when changeType changes
  useEffect(() => {
    if ((op === "within" || op === "before" || op === "relative") && changeType !== "relative") {
      setSelectOp(undefined);
    } else if ((op === "eq" || op === "gt" || op === "lt" || op === "range") && changeType !== "absolute") {
      setSelectOp(undefined);
    }
  }, [changeType, op]);

  // Generate stable IDs
  const firstDateId = useMemo(() => `date-filter-first-${op || 'empty'}`, [op]);
  const secondDateId = useMemo(() => `date-filter-second-${op || 'empty'}`, [op]);

  // Call handleChange directly in onChange handlers (user interaction only)
  const handleChangeType = useCallback((val: any) => {
    setChangeType(val);
  }, []);

  const handleSelectOpChange = useCallback((val: any) => {
    setSelectOp(val);
    // Call handleChange immediately for user interaction
    if (changeType === "relative") {
      // Will be called when relativeRange changes
    } else if (changeType === "absolute" && val) {
      if (val !== "range" && firstDate) {
        handleChange(val, dateConverter(new Date(firstDate)), "timestamp", "column");
      } else if (val === "range" && firstDate && secondDate) {
        const startDate = new Date(firstDate);
        const endDate = new Date(secondDate);
        // Validate: start date must be greater than (later than) end date
        if (startDate > endDate) {
          handleChange(val, {
            start: dateConverter(startDate),
            end: dateConverter(endDate),
          }, "timestamp", "column");
        }
        // If start <= end, don't call handleChange (invalid range)
      }
    }
  }, [changeType, firstDate, secondDate, handleChange]);

  const handleRelativeRangeChange = useCallback((val: any) => {
    setRelativeRange(val);
    // Call handleChange immediately for user interaction
    if (selectOp && val) {
      handleChange(selectOp, val, "timestamp", "column");
    }
  }, [selectOp, handleChange]);

  const handleFirstDateChange = useCallback((e: any) => {
    const newDate = e.target.value;
    setFirstDate(newDate);
    // Call handleChange immediately for user interaction
    if (changeType === "absolute" && selectOp && newDate) {
      if (selectOp !== "range") {
        handleChange(selectOp, dateConverter(new Date(newDate)), "timestamp", "column");
      } else if (secondDate) {
        const startDate = new Date(newDate);
        const endDate = new Date(secondDate);
        // Validate: start date must be greater than (later than) end date
        if (startDate > endDate) {
          handleChange(selectOp, {
            start: dateConverter(startDate),
            end: dateConverter(endDate),
          }, "timestamp", "column");
        }
        // If start <= end, don't call handleChange (invalid range)
      }
    }
  }, [changeType, selectOp, secondDate, handleChange]);

  const handleSecondDateChange = useCallback((e: any) => {
    const newDate = e.target.value;
    setSecondDate(newDate);
    // Call handleChange immediately for user interaction
    if (changeType === "absolute" && selectOp === "range" && firstDate && newDate) {
      const startDate = new Date(firstDate);
      const endDate = new Date(newDate);
      // Validate: start date must be greater than (later than) end date
      if (startDate > endDate) {
        handleChange(selectOp, {
          start: dateConverter(startDate),
          end: dateConverter(endDate),
        }, "timestamp", "column");
      }
      // If start <= end, don't call handleChange (invalid range)
    }
  }, [changeType, selectOp, firstDate, handleChange]);

  return (
    <>
      <Select
        placeholder="Select Change Type"
        data={[
          { value: "relative", label: "relative date" },
          { value: "absolute", label: "absolute date" },
        ]}
        value={changeType}
        onChange={handleChangeType}
        size="xs"
        classNames={FILTER_SELECT_CLASSES}
        comboboxProps={FILTER_COMBOBOX_PROPS}
      />
      {changeType === "relative" ? (
        <>
          <Select
            placeholder="Select Interval"
            data={ops}
            value={selectOp}
            onChange={handleSelectOpChange}
            size="xs"
            classNames={FILTER_SELECT_CLASSES}
            comboboxProps={FILTER_COMBOBOX_PROPS}
          />
          <Select
            placeholder="Last ..."
            data={TIME_FRAME}
            value={relativeRange}
            onChange={handleRelativeRangeChange}
            size="xs"
            classNames={FILTER_SELECT_CLASSES}
            comboboxProps={FILTER_COMBOBOX_PROPS}
          />
        </>
      ) : (
        <>
          <Select
            placeholder="Select Operation"
            data={ops}
            value={selectOp}
            onChange={handleSelectOpChange}
            disabled={fieldSelect}
            size="xs"
            classNames={FILTER_SELECT_CLASSES}
            comboboxProps={FILTER_COMBOBOX_PROPS}
          />
          <FormInput
            disabled={!selectOp}
            type="date"
            className="border-0"
            placeHolder="Select Date"
            id={firstDateId}
            value={firstDate}
            onChange={handleFirstDateChange}
          />
          {selectOp === "range" && (
            <FormInput
              type="date"
              className="border-0"
              placeHolder="Select end date"
              id={secondDateId}
              value={secondDate}
              onChange={handleSecondDateChange}
            />
          )}
        </>
      )}
    </>
  );
});

DateFilter.displayName = "DateFilter";

export default DateFilter;
